import { type FC } from "react";

import { DropdownMenu } from "@moment/design-system";
import {
	ArrowDropDownIcon,
	BookmarkIcon,
	DeleteIcon,
	DragIndicatorIcon,
	NorthEastIcon,
	PictureInPictureIcon,
	SettingsIcon,
	SouthEastIcon,
	SouthWestIcon,
	VisibilityIcon,
	VisibilityOffIcon,
} from "@moment/icons";

import { Button, IconButton } from "../../Button";
import { Input } from "../../Input";
import { useNotAutofocusableTabIndex } from "../../utils";
import { type BaseViewHandle } from "../BaseViewHandle";
import ColumnItemEndAddon from "../ColumnItemEndAddon";
import { ColumnTypeIcon } from "../ColumnTypeIcon";
import { type HydratedColumnDef } from "../useHydratedColumnDefs";
import { useRenameColumnState } from "./useRenameColumnState";

export const ColumnItem: FC<{
	viewHandle: BaseViewHandle;
	column: HydratedColumnDef;
	openEditColumnPopover: () => void;
}> = ({ column, viewHandle, openEditColumnPopover }) => {
	const { columnDef, displayOptions } = column;
	const type = columnDef.typeInfo?.enrichedType ?? "Text";
	const primaryDisplayColumn = displayOptions?.primaryDisplayColumn ?? false;
	const visible = displayOptions?.visibleOnCard || displayOptions?.visibleOnCard === undefined;

	const preventAutofocus = useNotAutofocusableTabIndex();

	const {
		currentHeaderValue,
		internalHeaderValue,
		setInternalHeaderValue,
		headerChanged,
		newColumnName,
	} = useRenameColumnState(viewHandle, column);

	return (
		<div className="flex flex-col gap-0">
			<div className="group/column flex gap-2 items-center">
				<div className="flex gap-0 items-center">
					<div className="ml-0.5 group-hover/column:opacity-100 opacity-0 flex text-palette-gray-9">
						<DragIndicatorIcon />
					</div>
					<IconButton
						// We want the popover to focus the first text input, not the first visible icon
						// button.
						tabIndex={preventAutofocus}
						preset="quaternary"
						size="sm"
						icon={visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
						// borderRadius="default"
						label="Show column"
						onClick={() =>
							viewHandle.updateColumnDisplayOptions(columnDef.name, {
								visibleOnCard: !visible,
							})
						}
					/>
				</div>
				<Input
					value={internalHeaderValue}
					onChange={(e) => setInternalHeaderValue(e.target.value)}
					onBlur={() => {
						if (!newColumnName) {
							return;
						}
						viewHandle.renameColumn(
							columnDef.name,
							internalHeaderValue ?? "",
							newColumnName
						);
					}}
					endAddon={<ColumnItemEndAddon displayOptions={displayOptions} />}
				/>
				<DropdownMenu.Root>
					<DropdownMenu.Trigger asChild>
						<Button
							className="!pl-1.5 !pr-0.5"
							preset="quaternary"
							size="md"
							startIcon={<ColumnTypeIcon type={type} />}
							borderRadius="default"
							aria-label="Edit column options"
							endIcon={<ArrowDropDownIcon className="-ml-2" />}
						/>
					</DropdownMenu.Trigger>
					<DropdownMenu.Content>
						<DropdownMenu.Item onSelect={openEditColumnPopover}>
							<SettingsIcon />
							Edit Column
						</DropdownMenu.Item>
						<DropdownMenu.Separator />
						<DropdownMenu.Item
							disabled={primaryDisplayColumn}
							disabledMessage="This column is already the primary display column"
							onSelect={() =>
								viewHandle.updateColumnDisplayOptions(columnDef.name, {
									primaryDisplayColumn: true,
								})
							}
						>
							<BookmarkIcon /> Set as display column
						</DropdownMenu.Item>
						<DropdownMenu.Sub>
							<DropdownMenu.SubTrigger>
								<PictureInPictureIcon />
								Position
							</DropdownMenu.SubTrigger>
							<DropdownMenu.SubContent>
								<DropdownMenu.RadioGroup
									value={displayOptions?.placementHint}
									onValueChange={(value) =>
										viewHandle.updateColumnDisplayOptions(columnDef.name, {
											placementHint:
												value === ""
													? undefined
													: (value as
															| "top-right"
															| "bottom-right"
															| "bottom-left"),
										})
									}
								>
									<DropdownMenu.RadioItem
										value={""}
										className="text-secondary italic"
									>
										None
									</DropdownMenu.RadioItem>
									<DropdownMenu.RadioItem value="top-right">
										Top right <NorthEastIcon className="ml-auto" />
									</DropdownMenu.RadioItem>
									<DropdownMenu.RadioItem value="bottom-right">
										Bottom right <SouthEastIcon className="ml-auto" />
									</DropdownMenu.RadioItem>
									<DropdownMenu.RadioItem value="bottom-left">
										Bottom left <SouthWestIcon className="ml-auto" />
									</DropdownMenu.RadioItem>
								</DropdownMenu.RadioGroup>
							</DropdownMenu.SubContent>
						</DropdownMenu.Sub>
						<DropdownMenu.Separator />
						<DropdownMenu.Item
							variant="negative"
							onSelect={() => viewHandle.deleteColumn(columnDef.name)}
						>
							<DeleteIcon /> Delete Column
						</DropdownMenu.Item>
					</DropdownMenu.Content>
				</DropdownMenu.Root>
			</div>
			{headerChanged && (
				<div className="text-xs text-secondary my-1 ml-14 mr-14">
					⇢ and migrate data from{" "}
					<code className="font-medium">.{currentHeaderValue}</code> to{" "}
					<code className="font-medium">.{newColumnName}</code>
				</div>
			)}
		</div>
	);
};
